import {
  Application,
  ApplicationAction,
  ApplicationActionType,
  ApplicationStep,
  Catalogue,
} from '../../../common';
import { Address, AddressSummaryCard } from '../address-summary-card/address-summary-card';
import { useDeliveryDetails } from '../../hooks/use-delivery-details';
import React, { useMemo, useState } from 'react';
import {
  getCatalogueItemFromCatalogue,
  isVirtualDevice,
} from '../../../common/helpers/get-device-from-catalogue';
import { Solution } from '../../../common/types/application/opportunity';
import { DeliveryAddressModal } from '../delivery-address-modal/delivery-address-modal';
import { LinkName } from '../../services/order-tealium-services';
import { yourDetailsRoute } from '../../helpers/routes';
import { PRODUCT_CATEGORY_NAME_MAP } from '../../tech-debt/product-details-extra';
import { findAddress } from '../../helpers/delivery-helpers';
import { useApplication } from 'src/hooks/useApplication';

export type DeliveryAddressFormProps = {
  application: Application;
  dispatch: React.Dispatch<ApplicationAction>;
  catalogue: Catalogue;
  automationIdSuffix: string;
};

export function DeliveryAddressForm({
  application,
  dispatch,
  catalogue,
  automationIdSuffix,
}: DeliveryAddressFormProps) {
  const deliveryDetails = useDeliveryDetails(application, catalogue);
  const { orderTealiumService } = useApplication();

  const [isDeliveryAddressModalOpen, setIsDeliveryAddressModalOpen] = useState<boolean>(false);
  const [indexOfEditingAddress, setIndexOfEditingAddress] = useState<number | undefined>(undefined);

  const addresses: Address[] = useMemo(() => {
    const addressesNew: Address[] = [];

    if (deliveryDetails.defaultAddress) {
      // filter out tender types
      const defaultAddressSolutionsFiltered = deliveryDetails.defaultAddress.solutions.filter(
        (solution) => {
          const catalogueItem = getCatalogueItemFromCatalogue(
            catalogue,
            solution.productId,
            solution.acquiringChannel,
          );
          return (
            catalogueItem &&
            (!catalogueItem.device.productCategoryName || catalogueItem.device.isProductCategory)
          );
        },
      );

      // Filter out virtual devices
      const defaultAddressSolutions = defaultAddressSolutionsFiltered.filter((solution) => {
        const catalogueItem = getCatalogueItemFromCatalogue(
          catalogue,
          solution.productId,
          solution.acquiringChannel,
        );

        return !isVirtualDevice(catalogueItem?.device.title ?? '');
      });

      if (defaultAddressSolutions.length > 0) {
        addressesNew.push({
          heading: 'Default address',
          sideNote: '(Unassigned devices will be sent to this address)',
          addressLines: [
            application.contact?.businessAddress?.street || '',
            application.contact?.businessAddress?.city || '',
            application.contact?.businessAddress?.province || '',
            application.contact?.businessAddress?.postalCode || '',
          ],
          contact: {
            name: application.contact?.name || '',
            id: application.contact?.idNumber || '',
            cellNumber: application.contact?.mobileNumber || '',
          },
          productDetails: {
            devices: getTitleListFromSolutions(defaultAddressSolutions),
            quantity: getTotalDevicesFromSolution(defaultAddressSolutions),
          },
          solution: deliveryDetails.defaultAddress?.solutions[0],
          productsDescription: deliveryDetails.defaultAddress
            ? getTitleListFromSolutions(defaultAddressSolutions)
            : 'All',
          isEditButtonEnabled: true,
          isDeleteButtonEnabled: false,
          onDeleteButtonClicked: () => {},
          onEditButtonClicked: () => {
            const yourDetailsStep: ApplicationStep | undefined = application.steps?.find(
              (step) => step.route === yourDetailsRoute,
            );

            if (!yourDetailsStep) {
              throw new Error(
                `Delivery Address revamp: step name not supported: ${yourDetailsRoute} not found`,
              );
            }

            dispatch({
              type: ApplicationActionType.QUICK_EDIT,
              steps: [yourDetailsStep],
            });
          },
        });
      }
    }

    if (deliveryDetails.customAddresses) {
      deliveryDetails.customAddresses.forEach((delivery, index) => {
        if (!delivery.solutions?.[0]?.productId) return;

        addressesNew.push({
          heading: `Address ${index + 1}`,
          addressLines: [
            delivery?.address.street || '',
            delivery?.address.city || '',
            delivery?.address.province || '',
            delivery?.address.postalCode || '',
          ],
          contact: {
            name: delivery?.receiverDetails.managerName || '',
            id: delivery?.receiverDetails.idNumber || '',
            cellNumber: delivery?.receiverDetails.cellPhone || '',
            workNumber: delivery?.receiverDetails.workPhone || '',
          },
          tradingName: delivery.receiverDetails.tradingAs || '',
          productDetails: {
            devices: getTitleListFromSolutions(delivery.solutions),
            quantity: getTotalDevicesFromSolution(delivery.solutions),
          },
          solution: delivery.solutions[0],
          productsDescription: getTitleListFromSolutions(delivery.solutions),
          isEditButtonEnabled: true,
          isDeleteButtonEnabled: true,
          onDeleteButtonClicked: () => {
            orderTealiumService?.onOrderSummaryAction(LinkName.REMOVE_DELIVERY_ADDRESS);
            const newDeliveries = [...(application.opportunity?.deliveries || [])];
            newDeliveries.splice(index, 1);
            dispatch({ type: ApplicationActionType.UPDATE_DELIVERIES, deliveries: newDeliveries });
          },
          onEditButtonClicked: () => {
            orderTealiumService?.onOrderSummaryAction(LinkName.EDIT_DELIVERY_ADDRESS);
            setIndexOfEditingAddress(index);
            setIsDeliveryAddressModalOpen(true);
          },
        });
      });
    }

    return addressesNew;
  }, [deliveryDetails]);

  const onAddAddressButtonClicked = () => {
    orderTealiumService?.onOrderSummaryAction(LinkName.ADD_DELIVERY_ADDRESS);
    orderTealiumService?.onDeliveryAddressStart();
    setIndexOfEditingAddress(undefined);
    setIsDeliveryAddressModalOpen(true);
  };
  const isBrokerLogin = !!application.brokerLogin;
  const isAgentJourney = !!application.agentJourney;
  const isAddAddressButtonEnabled: boolean =
    catalogue.products.find(
      (item) =>
        item.device.quantity > 0 && !PRODUCT_CATEGORY_NAME_MAP[item.device.productCategoryName],
    ) !== undefined;

  return (
    <>
      <AddressSummaryCard
        className={`address-container ${isBrokerLogin ? 'broker-login-container' : ''}`}
        addresses={addresses}
        isBrokerLogin={isBrokerLogin}
        isAddAddressButtonEnabled={isAddAddressButtonEnabled}
        onAddAddressButtonClicked={onAddAddressButtonClicked}
        deliveryDetails={deliveryDetails}
        application={application}
        dispatch={dispatch}
        catalogue={catalogue}
        automationid={automationIdSuffix}
      />
      <DeliveryAddressModal
        application={application}
        show={isDeliveryAddressModalOpen}
        setShow={setIsDeliveryAddressModalOpen}
        deliveryDetails={deliveryDetails}
        isBrokerEditAddress={false}
        catalogue={catalogue}
        automationIdSuffix={automationIdSuffix}
        onSave={(delivery, deliveries) => {
          if (deliveries && (isBrokerLogin || isAgentJourney)) {
            dispatch({ type: ApplicationActionType.UPDATE_DELIVERIES, deliveries: deliveries });
            setIndexOfEditingAddress(undefined);
            setIsDeliveryAddressModalOpen(false);
          } else if (indexOfEditingAddress !== undefined) {
            const newDeliveries = [...(application.opportunity?.deliveries || [])];
            newDeliveries[indexOfEditingAddress] = delivery;
            dispatch({ type: ApplicationActionType.UPDATE_DELIVERIES, deliveries: newDeliveries });
            setIndexOfEditingAddress(undefined);
            setIsDeliveryAddressModalOpen(false);
          } else {
            const newDeliveries = [...(application.opportunity?.deliveries || [])];
            newDeliveries.push(delivery);
            dispatch({ type: ApplicationActionType.UPDATE_DELIVERIES, deliveries: newDeliveries });
            setIndexOfEditingAddress(undefined);
            setIsDeliveryAddressModalOpen(false);
          }
        }}
        valuesToEdit={
          indexOfEditingAddress !== undefined && deliveryDetails?.customAddresses
            ? deliveryDetails.customAddresses[indexOfEditingAddress]
            : undefined
        }
        saveAddress={(delivery) => {
          if (findAddress(delivery.address, application.opportunity?.deliveries || [])) {
            const updatedDeliveries = application.opportunity?.deliveries?.map((_delivery) => {
              if (findAddress(delivery.address, [_delivery])) {
                return {
                  ..._delivery,
                  ...delivery,
                };
              }

              return _delivery;
            });

            dispatch({
              type: ApplicationActionType.UPDATE_DELIVERIES,
              deliveries: updatedDeliveries || [],
            });
            return;
          }

          const newDeliveries = [...(application.opportunity?.deliveries || [])];
          newDeliveries.push(delivery);
          dispatch({ type: ApplicationActionType.UPDATE_DELIVERIES, deliveries: newDeliveries });
        }}
      />
    </>
  );

  function getTitleListFromSolutions(solutions: Solution[]) {
    return solutions
      .map((solution) => {
        const catalogueItem = getCatalogueItemFromCatalogue(
          catalogue,
          solution.productId,
          solution.acquiringChannel,
        );

        if (!catalogueItem) {
          throw new Error(
            `Delivery address revamp: device referenced in delivery address is not found: product id: ${solution.productId}`,
          );
        }

        return catalogueItem.device.title;
      })
      .join(', ');
  }

  function getTotalDevicesFromSolution(solutions: Solution[]): number {
    return solutions.reduce<number>((total, solution) => {
      const catalogueItem = getCatalogueItemFromCatalogue(
        catalogue,
        solution.productId,
        solution.acquiringChannel,
      );

      if (!catalogueItem) {
        throw new Error(
          `Delivery address revamp: device referenced in delivery address is not found: product id: ${solution.productId}`,
        );
      }

      return total + solution.quantity;
    }, 0);
  }
}
